import { useAbility } from '@casl/react';
import { Avatar, Box, Flex, TabList, TabPanel, TabPanels, Tabs, Text, Link, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import Pill from 'components/Pill/Pill';
import { switchProfileTo } from 'modules/user/userSlice';
import { rem } from 'polished';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { RootState } from 'store';
import { AbilityContext } from 'utils/permissions/Can';
import { isGuestContextSelector } from 'modules/app/appSlice';

interface Props {
  profileItems: MenuItem[];
  companyItems: MenuItem[];
  customIndex?: number;
  onItemClick?: () => void;
}

const activeClassName = 'nav-item-active';
const List = styled.ul`
  margin: 0;
  padding: 15px 0 50px 0;
  list-style: none;
`;

const Item = styled(Box)<{ disabled?: boolean }>`
  display: flex;
  padding: 10px 0;
  height: 100%;
  align-items: center;
  margin: 0;
  opacity: ${(props): number => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props): string => (props.disabled ? 'none' : 'auto')};
`;

const NavItem = styled(Link)`
  display: flex;
  width: 100%;
  position: relative;
  padding: 1px 0;
  align-items: center;
  opacity: 0.5;
  transition: all 0.2s ease-in-out;
  font-size: ${rem(14)};
  font-weight: normal;

  > div {
    margin-right: 15px;
    position: relative;
    top: -1px;
  }

  &.${activeClassName} {
    opacity: 1;
    font-weight: 500;

    &:before {
      content: '';
      position: absolute;
      left: -25px;
      top: 0;
      height: 100%;
      width: 3px;
    }
  }
`;

const ProfileSwitcher: FC<Props> = ({ profileItems, companyItems, customIndex, onItemClick }) => {
  const { colors } = useTheme();
  const { t } = useTranslation('common');
  const { currentAccount, currentProfile } = useSelector((state: RootState) => state.user);
  const context = useSelector((state: RootState) => state.app.context);
  const dispatch = useDispatch();
  const isGuest = useSelector(isGuestContextSelector);
  const isNotGuest = !isGuest;

  const profileTypes: ProfileType[] = ['Person', 'Company'];
  function handleProfileTab(index: number): void {
    dispatch(switchProfileTo(profileTypes[index]));
  }

  function getTabProfileIndex(): number {
    return profileTypes.findIndex(profileType => profileType === currentProfile?.type);
  }

  const customer = context === 'connect' ? currentAccount?.partnership?.customer : currentAccount?.customer;

  const ability = useAbility(AbilityContext);

  const isWeChoooseContext = ['wechooose', 'connect'].includes(context);
  const hasCustomerOrIsPartnership = context === 'connect' || (!!customer && context === 'wechooose');

  const canViewCompanySettings = ability.can('view', 'CompanySettings') || context === 'connect';
  const hasAccessToCompanySettings = canViewCompanySettings && isWeChoooseContext && hasCustomerOrIsPartnership && companyItems.length > 0;

  return (
    <Tabs w="100%" onChange={handleProfileTab} index={customIndex ?? getTabProfileIndex()} isFitted align="center" data-testid="profile-tabs">
      {hasAccessToCompanySettings ? (
        <TabList layerStyle={'profileSwitcherTabs'}>
          <Pill>{t('profileMenu.you')}</Pill>
          <Pill>{t('profileMenu.company')}</Pill>
        </TabList>
      ) : null}
      <TabPanels>
        {currentAccount ? (
          <TabPanel p="0px 25px">
            <Flex align="center">
              <Avatar
                name={isNotGuest ? currentAccount?.profile?.name : undefined}
                src={isNotGuest ? currentAccount?.profile?.pictureUrl : undefined}
              />
              <Text fontSize="lg" ml="15px" layerStyle="profileSwitcherYourAccountText">
                {t('profileMenu.yourAccount')}
              </Text>
            </Flex>
            <List>
              {profileItems.map((item: MenuItem) => (
                <Item as="li" key={item.text} disabled={item.disabled}>
                  <NavItem
                    as={NavLink}
                    exact
                    to={item.link}
                    activeClassName={activeClassName}
                    sx={{
                      color: colors.greyLinkActive,
                      '&:hover': {
                        textDecoration: 'none',
                      },
                      '& > div > svg': {
                        stroke: colors.greyLinkActive,
                      },
                      [`&.${activeClassName}`]: {
                        color: colors.pink[500],
                        '&:before': {
                          bg: colors.pink[500],
                        },
                        '& > div > svg': {
                          stroke: colors.pink[500],
                        },
                      },
                    }}
                    onClick={onItemClick}
                  >
                    {item.text}
                  </NavItem>
                </Item>
              ))}
            </List>
          </TabPanel>
        ) : null}
        {hasAccessToCompanySettings ? (
          <TabPanel p="0px 25px">
            <Flex align="center">
              <Avatar name={context === 'connect' ? currentAccount?.partnership?.name : currentAccount?.customer?.name} />
              <Text fontSize="lg" layerStyle="profileSwitcherYourAccountText" ml="15px">
                {t('profileMenu.yourAccount')}
              </Text>
            </Flex>
            <List>
              {companyItems.map((item: MenuItem) => (
                <Item as="li" key={item.text} disabled={item.disabled}>
                  <NavItem
                    as={NavLink}
                    exact
                    to={item.link}
                    activeClassName={activeClassName}
                    sx={{
                      color: colors.greyLinkActive,
                      '&:hover': {
                        textDecoration: 'none',
                      },
                      '& > div > svg': {
                        stroke: colors.greyLinkActive,
                      },
                      [`&.${activeClassName}`]: {
                        color: colors.pink[500],
                        '&:before': {
                          bg: colors.pink[500],
                        },
                        '& > div > svg': {
                          stroke: colors.pink[500],
                        },
                      },
                    }}
                    onClick={onItemClick}
                  >
                    {item.text}
                  </NavItem>
                </Item>
              ))}
            </List>
          </TabPanel>
        ) : null}
      </TabPanels>
    </Tabs>
  );
};

export default ProfileSwitcher;
